import { DateUtil, isNullOrUndefined } from '@uzurv/core';
import { autoserialize, autoserializeAs } from 'cerialize';
import moment from 'moment-timezone';
import { LyftDispatchRideStatus } from './lyft-dispatch-ride-status';
import { LyftDispatchRideType } from './lyft-dispatch-ride-type';
import { LyftRendesvous } from './lyft-rendezvous';
import { RideRequestLocation } from './ride-request-location';
import { RideRequestType } from './ride-request-type';

export class LyftDispatchRideTrackerDetail {
  @autoserialize
  rideRequestId: number;

  @autoserialize
  itineraryId: number;

  @autoserialize
  riderName: string;

  @autoserialize
  riderPhoneNumber: string;

  @autoserialize
  smsEnabledPhone: boolean;

  @autoserializeAs(Date)
  pickupDate: Date;

  @autoserialize
  duration: number;

  @autoserialize
  polyline: string;

  @autoserializeAs(RideRequestLocation)
  pickupLocation: RideRequestLocation;

  @autoserializeAs(RideRequestLocation)
  dropoffLocation: RideRequestLocation;

  @autoserialize
  routeDistance: number;

  @autoserialize
  isAccepted: boolean;

  @autoserializeAs(Date)
  cancelledDate: Date;

  @autoserialize
  transitProgramId: number;

  @autoserialize
  transitProgramIdentifier: string;

  @autoserialize
  rideRequestCancelled: boolean;

  @autoserialize
  timeZone: string;

  @autoserialize
  riderNote: string;

  @autoserialize
  reservationId: number;

  @autoserialize
  lyftDispatchRideId: number;

  @autoserialize
  lyftRideId: string;

  @autoserializeAs(LyftDispatchRideType)
  lyftDispatchRideType: LyftDispatchRideType;

  @autoserializeAs(LyftDispatchRideStatus)
  lyftDispatchRideStatus: LyftDispatchRideStatus;

  @autoserialize
  routeUrl: string;

  @autoserialize
  externalNote: string;

  @autoserialize
  primeTimePercentage: number;

  @autoserialize
  driverName: string;

  @autoserialize
  driverPhoneNumber: string;

  @autoserialize
  driverAvatarUrl: string;

  @autoserialize
  beaconColor: string;

  @autoserialize
  vehicleColor: string;

  @autoserialize
  vehicleMake: string;

  @autoserialize
  vehicleModel: string;

  @autoserialize
  vehicleYear: string;

  @autoserialize
  vehicleImageUrl: string;

  @autoserialize
  licensePlate: string;

  @autoserialize
  licensePlateState: string;

  @autoserializeAs(Date)
  requestedAt: Date;

  @autoserializeAs(Date)
  generatedAt: Date;

  @autoserialize
  createdBy: string;

  @autoserializeAs(LyftRendesvous)
  driverArrived: LyftRendesvous;

  @autoserializeAs(LyftRendesvous)
  pickUp: LyftRendesvous;

  @autoserializeAs(LyftRendesvous)
  dropOff: LyftRendesvous;

  @autoserialize
  canceledBy: string;

  @autoserializeAs(Date)
  createdDate: Date;

  @autoserializeAs(Date)
  itineraryPickupDate: Date;

  @autoserializeAs(RideRequestType)
  rideRequestType: RideRequestType;

  @autoserializeAs(Date)
  activatedAt: Date;

  @autoserializeAs(Date)
  recoveryRideCreatedDate: Date;

  get displayDate(): Date {
    if (!this.rideRequestType.onDemand) {
      return this.pickupDate;
    }
    return this.itineraryPickupDate;
  }

  /**
   * TODO: Refactor later using https://www.npmjs.com/package/localdate
   */
  get formattedDisplayDate(): string {
    if (!this.rideRequestType.onDemand) {
      return moment.tz(this.pickupDate, this.timeZone).format('M/DD/YY h:mm a (z)');
    }
    return moment(this.itineraryPickupDate).format('M/DD/YY');
  }

  static OnSerialized(instance: LyftDispatchRideTrackerDetail, json: any): void {
    if (!isNullOrUndefined(instance.pickupDate)) {
      json.pickupDate = DateUtil.serializeDate(instance.pickupDate);
    }

    if (!isNullOrUndefined(instance.cancelledDate)) {
      json.cancelledDate = DateUtil.serializeDate(instance.cancelledDate);
    }

    if (!isNullOrUndefined(instance.requestedAt)) {
      json.requestedAt = DateUtil.serializeDate(instance.requestedAt);
    }

    if (!isNullOrUndefined(instance.generatedAt)) {
      json.generatedAt = DateUtil.serializeDate(instance.generatedAt);
    }

    if (!isNullOrUndefined(instance.createdDate)) {
      json.createdDate = DateUtil.serializeDate(instance.createdDate);
    }

    if (!isNullOrUndefined(instance.itineraryPickupDate)) {
      json.itineraryPickupDate = DateUtil.serializeLocalDate(instance.itineraryPickupDate);
    }

    if (!isNullOrUndefined(instance.activatedAt)) {
      json.activatedAt = DateUtil.serializeDate(instance.activatedAt);
    }

    if (!isNullOrUndefined(instance.recoveryRideCreatedDate)) {
      json.recoveryRideCreatedDate = DateUtil.serializeDate(instance.recoveryRideCreatedDate);
    }

    if (!isNullOrUndefined(instance.riderNote)) {
      const riderNote: string = instance.riderNote.trim();
      json.riderNote = riderNote.length === 0 ? null : riderNote;
    }
  }

  static OnDeserialized(instance: LyftDispatchRideTrackerDetail, json: any): void {
    if (!isNullOrUndefined(json['pickupDate'])) {
      instance.pickupDate = DateUtil.deserializeDate(json['pickupDate']);
    }

    if (!isNullOrUndefined(json['cancelledDate'])) {
      instance.cancelledDate = DateUtil.deserializeDate(json['cancelledDate']);
    }

    if (!isNullOrUndefined(json['requestedAt'])) {
      instance.requestedAt = DateUtil.deserializeDate(json['requestedAt']);
    }

    if (!isNullOrUndefined(json['generatedAt'])) {
      instance.generatedAt = DateUtil.deserializeDate(json['generatedAt']);
    }

    if (!isNullOrUndefined(json['createdDate'])) {
      instance.createdDate = DateUtil.deserializeDate(json['createdDate']);
    }

    if (!isNullOrUndefined(json['itineraryPickupDate'])) {
      instance.itineraryPickupDate = DateUtil.deserializeLocalDate(json['itineraryPickupDate']);
    }

    if (!isNullOrUndefined(json['activatedAt'])) {
      instance.activatedAt = DateUtil.deserializeDate(json['activatedAt']);
    }

    if (!isNullOrUndefined(json['recoveryRideCreatedDate'])) {
      instance.recoveryRideCreatedDate = DateUtil.deserializeDate(json['recoveryRideCreatedDate']);
    }
  }
}
